import React, { useCallback, useEffect, useMemo } from 'react';
import { Grid, Menu, MenuItem, Typography, IconButton, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import _, { get, isEmpty, debounce, isFunction } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  Clear as ClearIcon,
  Search as PageviewIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AddCircle as AddCircleIcon,
  MoreHoriz as MoreHorizIcon,
} from '@material-ui/icons';
import { useForm, useFormState } from 'react-final-form';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import QCXButton from '../button/QCXButton';
import { ERROR_SEVERITY, SUCCESS_SEVERITY, WARNING_SEVERITY } from '../../features/severity';
import { setErrorFeedback, setSuccessFeedback, setWarningFeedback } from '../../features/feedback/feedbackSlice';
import QCXDataGrid from '../data-grid/QCXDataGrid';
import QCXPopupState from '../../components/popup-state/QCXPopupState';
import QCXListItemIcon from '../list-item-icon/QCXListItemIcon';
import { isLoadingStatus, isNoneMode, isConsultMode, isCreateMode, isUpdateMode } from '../../utils/store/store-utils';
import QCXCircularProgress from '../progress/QCXCircularProgress';
import useOperationMiddleware from '../../utils/hooks/operation/middleware/useOperationMiddleware';
import QCXConfirmDialog from '../dialog/QCXConfirmDialog';
import useRequiredValidator from '../../utils/hooks/form/field/useRequiredValidator';

const useStyles = makeStyles(() => ({
  dataGridDiv: {
    width: 'calc(100% - 0px) !important',
  },
}));

/**
 * @deprecated. Será  deletado quando todos os componentes forem substituidos.
 * Se tiver a oportunidade substitua pela nova versão.
 * Use <QCXFinalBondManager /> instead.
 */
export default function QCXFinalBondManagerOld({
  isParentConsult,
  listProps,
  formProps,
  reducerConfig,
  generalConfig = {},
  handleAdd,
  handleUpdate,
  handleRemove,
  handleAlreadyExists,
  onClearDecorator,
  onConsultDecorator,
  onAddDecorator,
  disableMultipleFields = false,
  disableConsult = false,
  disableRemove = false,
  disableDefaultRemove = false,
  disableButtons = false,
  hideAddButton = false,
  onlyShowFieldOnEdit = false,
  readOnlyMode = false,
  forceShowDelete = false,
  controlButtonsGridProps,
  dataGridDivProps = {},
  children,
}) {
  const { t } = useTranslation();
  const classes = useStyles();

  const dispatch = useDispatch();

  const requiredValidator = useRequiredValidator({
    options: {
      showFeedback: true,
    },
  });

  const internalControlButtonsGridProps = useMemo(
    () =>
      isEmpty(controlButtonsGridProps)
        ? {
            add: {
              xs: 12,
              sm: 4,
              md: 4,
            },
            update: {
              xs: 12,
              sm: 2,
              md: 2,
            },
            clear: {
              xs: 6,
              sm: readOnlyMode ? 4 : 2,
              md: readOnlyMode ? 4 : 2,
            },
          }
        : controlButtonsGridProps,
    [controlButtonsGridProps, readOnlyMode]
  );

  const {
    selectors: { selectStatus, selectMode, selectModel },
    actions: { loading, resetStatus, changeToUpdateMode, changeToCreateMode, resetMode, setModel, resetModel },
  } = useMemo(() => reducerConfig, [reducerConfig]);

  const actionConfig = useMemo(() => generalConfig?.actionConfig, [generalConfig]);

  const feedbackConfig = useMemo(() => generalConfig?.feedbackConfig, [generalConfig]);

  const status = useSelector(selectStatus);
  const mode = useSelector(selectMode);
  const model = useSelector(selectModel);

  const form = useForm();
  const { values } = useFormState();

  const isLoading = useMemo(() => isLoadingStatus(status), [status]);

  const isNone = useMemo(() => isNoneMode(mode), [mode]);

  const isCreate = useMemo(() => isCreateMode(mode), [mode]);

  const isConsult = useMemo(() => isConsultMode(mode), [mode]);

  const isUpdate = useMemo(() => isUpdateMode(mode), [mode]);

  const tools = useMemo(
    () => ({
      formUtils: {
        form,
        values,
      },
      stateUtils: {
        loading: () => {
          dispatch(loading());
        },
        resetStatus: () => {
          dispatch(resetStatus());
        },
        changeToCreateMode: () => {
          dispatch(changeToCreateMode());
        },
        changeToUpdateMode: () => {
          dispatch(changeToUpdateMode());
        },
        resetMode: () => {
          dispatch(resetMode());
        },
        setModel: (data) => {
          dispatch(setModel(data));
        },
        resetModel: () => {
          dispatch(resetModel());
        },
      },
    }),
    [form, values, loading, resetStatus, changeToUpdateMode, changeToCreateMode, resetMode, setModel, resetModel]
  );

  const vinculoListName = useMemo(() => listProps?.name, [listProps]);

  const fields = useMemo(() => formProps?.fields || [], [formProps]);

  const currentVinculo = useMemo(() => get(values, formProps?.rootName), [values]);

  const currentVinculosList = useMemo(() => get(values, vinculoListName) || [], [values, vinculoListName]);

  const isNoSameThatCurrent = useCallback(
    (current, data) =>
      fields.some((prop) => {
        const currentVinculoPropValue = get(current, prop?.name);
        const dataPropValue = get(data, prop?.name);
        return !!(currentVinculoPropValue !== dataPropValue);
      }),
    [fields]
  );

  const handleFeedback = useCallback((impediment) => {
    if (impediment?.severity === WARNING_SEVERITY) {
      dispatch(
        setWarningFeedback({
          message: t('com.muralis.qcx.atencaoMensagem', { mensagem: impediment?.message }),
        })
      );
      return;
    }

    if (impediment?.severity === ERROR_SEVERITY) {
      dispatch(
        setErrorFeedback({
          message: t('com.muralis.qcx.erroMensagem', { mensagem: impediment?.message }),
        })
      );
      return;
    }

    if (impediment?.severity === SUCCESS_SEVERITY) {
      dispatch(
        setSuccessFeedback({
          message: impediment?.message,
        })
      );
    }
  }, []);

  const handleSuccessAddVinculo = useCallback(
    (updatedVinculos) => {
      const successAddAction = actionConfig?.add?.success;

      if (!successAddAction?.disableUpdateList) {
        form.change(vinculoListName, updatedVinculos);
      }

      if (!successAddAction?.disableResetRootValues) {
        form.change(formProps?.rootName, undefined);
      }

      if (isFunction(onAddDecorator)) {
        onAddDecorator(tools);
      }
    },
    [form, actionConfig, feedbackConfig, setSuccessFeedback, vinculoListName, onAddDecorator]
  );

  const handleSuccessUpdateVinculo = useCallback(
    (updatedVinculos) => {
      const successUpdateAction = actionConfig?.update?.success;

      if (!successUpdateAction?.disableUpdateList) {
        form.change(vinculoListName, updatedVinculos);
      }

      const successUpdateFeedback = feedbackConfig?.update?.success;

      const message = successUpdateFeedback?.message
        ? successUpdateFeedback?.message
        : t('com.muralis.qcx.mensagem.vinculoAtualizado');

      handleFeedback({
        severity: SUCCESS_SEVERITY,
        message,
      });
    },
    [form, actionConfig, feedbackConfig, setSuccessFeedback, vinculoListName, currentVinculosList]
  );

  const handleSuccessRemoveVinculo = useCallback(
    (vinculo, options) => {
      const updatedVinculos = currentVinculosList?.filter((existingVinculo) => vinculo?.id !== existingVinculo?.id);

      form.change(vinculoListName, updatedVinculos);

      const successRemoveFeedback = feedbackConfig?.remove?.success;

      const message = successRemoveFeedback?.message
        ? successRemoveFeedback?.message
        : t('com.muralis.qcx.mensagem.itemRemovido');

      handleFeedback({
        severity: SUCCESS_SEVERITY,
        message,
      });

      if (isFunction(options?.onRemoveDecorator)) {
        options.onRemoveDecorator();
      }
    },
    [form, feedbackConfig, handleFeedback, vinculoListName, currentVinculosList]
  );

  const handleError = useCallback(
    (error) => {
      const errorMessage = error?.response?.message
        ? t('com.muralis.qcx.erro.erroRealizarVinculo', { mensagem: error?.response?.message })
        : error?.message;

      handleFeedback({
        severity: ERROR_SEVERITY,
        message: errorMessage,
      });
    },
    [handleFeedback]
  );

  const requiredFormFields = useMemo(
    () =>
      formProps?.fields
        ?.filter(({ required }) => required !== false)
        ?.map(({ name, label }) => ({
          name,
          label,
        })),
    [formProps]
  );

  const handleVincular = useCallback(
    async (event) => {
      event?.stopPropagation();

      const validationResult = requiredValidator.validate(requiredFormFields, currentVinculo);

      if (!validationResult?.valid) {
        return;
      }

      dispatch(loading());

      const handleDebouncedVincular = debounce(async () => {
        try {
          const alreadyExistsVinculo = isFunction(handleAlreadyExists)
            ? handleAlreadyExists(currentVinculo, currentVinculosList)
            : undefined;

          if (
            (alreadyExistsVinculo && !disableMultipleFields) ||
            (alreadyExistsVinculo && disableMultipleFields && !isUpdate)
          ) {
            const message = t('com.muralis.qcx.mensagem.vinculoInformacoesExiste');
            handleFeedback({
              severity: ERROR_SEVERITY,
              message,
            });

            dispatch(resetStatus());

            return;
          }

          if (isUpdate) {
            await handleUpdate(currentVinculo, currentVinculosList, handleSuccessUpdateVinculo, tools);

            return;
          }

          await handleAdd(currentVinculo, currentVinculosList, handleSuccessAddVinculo, tools);

          dispatch(resetModel());
        } catch (error) {
          handleError(error);
        } finally {
          dispatch(resetStatus());
        }
      }, 500);

      handleDebouncedVincular();
    },
    [
      form,
      tools,
      isUpdate,
      disableMultipleFields,
      currentVinculo,
      currentVinculosList,
      handleFeedback,
      requiredValidator,
      model,
      resetModel,
      requiredFormFields,
    ]
  );

  const handleConsultVinculo = useCallback(
    (event, data) => {
      if (event && isFunction(event?.stopPropagation)) {
        event?.stopPropagation();
      }

      if (isNoSameThatCurrent(currentVinculo, data)) {
        if (isFunction(onConsultDecorator)) {
          onConsultDecorator(tools);
        }

        dispatch(changeToUpdateMode());
        dispatch(loading());

        dispatch(setModel(data));

        form.change(formProps?.rootName, undefined);

        _.debounce(() => {
          form.change(formProps?.rootName, data);

          dispatch(resetStatus());
        }, 400)();
      }
    },
    [form, tools, fields, formProps, currentVinculo, onConsultDecorator, isNoSameThatCurrent]
  );

  const handleClearCurrentVinculo = useCallback(
    (event) => {
      if (isFunction(event?.stopPropagation)) {
        event?.stopPropagation();
      }

      form.change(formProps?.rootName, undefined);

      dispatch(resetModel());

      if (isFunction(onClearDecorator)) {
        onClearDecorator(tools);
      }

      if (isParentConsult) {
        dispatch(resetMode());
        return;
      }

      dispatch(changeToCreateMode());
    },
    [form, tools, formProps, isParentConsult, onClearDecorator]
  );

  const [handleRemoveVinculoByMiddleware, operationOfRemoveVinculo] = useOperationMiddleware(
    async (vinculo) => {
      try {
        if (disableDefaultRemove && isFunction(handleRemove)) {
          await handleRemove({
            target: vinculo,
            list: currentVinculosList,
            handleSuccess: handleSuccessRemoveVinculo,
            tools,
          });

          return;
        }

        handleSuccessRemoveVinculo(vinculo, {
          onRemoveDecorator: () => {
            if (isUpdate) {
              handleClearCurrentVinculo();
            }
          },
        });
      } catch (error) {
        handleError(error);
      }
    },
    [
      tools,
      isUpdate,
      handleError,
      handleRemove,
      currentVinculo,
      currentVinculosList,
      disableDefaultRemove,
      handleSuccessRemoveVinculo,
      handleClearCurrentVinculo,
    ]
  );

  const handleRemoveVinculoByRowClick = useCallback(
    (event, data) => {
      if (event && isFunction(event?.stopPropagation)) {
        event?.stopPropagation();
      }

      if (!isEmpty(data)) {
        if (isFunction(event?.stopPropagation)) {
          const titleOfOperation = actionConfig?.remove?.confirm?.title || t('com.muralis.qcx.acoes.confirmarOperacao');

          const messageOfOperation =
            actionConfig?.remove?.confirm?.message || t('com.muralis.qcx.mensagem.confirmarOperacaoMensagem');

          const configureOperation = () => ({
            options: {
              title: titleOfOperation,
              message: messageOfOperation,
            },
          });

          handleRemoveVinculoByMiddleware(configureOperation, data);
        }
      }
    },
    [currentVinculo, handleRemoveVinculoByMiddleware]
  );

  useEffect(() => {
    const handleChangeMode = () => {
      if (isParentConsult) {
        dispatch(resetMode());
      }
    };

    handleChangeMode();
  }, [isParentConsult]);

  const renderOtherMenuItems = useMemo(() => listProps?.renderOtherMenuItems, [listProps]);

  const controlBoxColumns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('com.muralis.qcx.acoes.label'),
        headerAlign: 'center',
        align: 'center',
        flex: 90,
        renderCell: ({ row }) => (
          <QCXPopupState popupId={`popup-menu-bond-manager-${row?.id}`}>
            {(popupState) => (
              <>
                <IconButton
                  key={`btn-more-options-${row?.id}`}
                  name={`btn-more-options-${row?.id}`}
                  {...bindTrigger(popupState)}
                >
                  <MoreHorizIcon color="secondary" size={20} />
                </IconButton>
                <Menu {...bindMenu(popupState)}>
                  {!disableConsult && (
                    <MenuItem
                      key={`btn-consulta-${row?.id}`}
                      name={`btn-consulta-${row?.id}`}
                      onClick={(event) => {
                        popupState.close(event);

                        handleConsultVinculo(event, row);
                      }}
                    >
                      <QCXListItemIcon>
                        <PageviewIcon fontSize="small" />
                      </QCXListItemIcon>
                      <Typography
                        variant="inherit"
                        style={{
                          fontSize: 12,
                        }}
                      >
                        {t('com.muralis.qcx.acoes.consultar').toUpperCase()}
                      </Typography>
                    </MenuItem>
                  )}
                  {isFunction(renderOtherMenuItems) && renderOtherMenuItems(row, popupState, isParentConsult, tools)}
                  {(forceShowDelete || !readOnlyMode) && (
                    <MenuItem
                      key={`btn-excluir-${row?.id}`}
                      name={`btn-excluir-${row?.id}`}
                      onClick={(event) => {
                        popupState.close(event);

                        handleRemoveVinculoByRowClick(event, row);
                      }}
                      disabled={disableRemove || isParentConsult}
                    >
                      <QCXListItemIcon>
                        <DeleteIcon fontSize="small" color={isParentConsult ? 'disabled' : 'error'} />
                      </QCXListItemIcon>
                      <Typography
                        variant="inherit"
                        style={{
                          fontSize: 12,
                        }}
                      >
                        {t('com.muralis.qcx.acoes.excluir').toUpperCase()}
                      </Typography>
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </QCXPopupState>
        ),
      },
    ],
    [tools, isParentConsult, handleRemoveVinculoByRowClick, handleConsultVinculo]
  );

  const dataGridColumns = useMemo(() => listProps?.columns.concat(controlBoxColumns), [listProps, controlBoxColumns]);

  const renderChildren = () =>
    isFunction(children)
      ? children({
          loading: isLoading,
          creating: isCreate,
          consulting: isConsult,
          updating: isUpdate,
        })
      : children;

  return (
    <>
      <Grid item container spacing={2}>
        {(onlyShowFieldOnEdit && isUpdate) || !onlyShowFieldOnEdit ? renderChildren() : <></>}
        {!readOnlyMode && !isConsult && (isNone || isCreate) && !hideAddButton && (
          <Grid item xs={12} sm={4} md={4} {...internalControlButtonsGridProps?.add}>
            <QCXButton
              id="control-box-add-button"
              key="control-box-add-button"
              color="secondary"
              onClick={handleVincular}
              disabled={disableButtons || isParentConsult || isConsult || isLoading}
              fullWidth
              tooltip
              tooltipDescription={t('com.muralis.qcx.acoes.adicionar')}
              startIcon={!isLoading && <AddCircleIcon />}
            >
              {isLoading ? <QCXCircularProgress color="disabled" size={20} /> : t('com.muralis.qcx.acoes.adicionar')}
            </QCXButton>
          </Grid>
        )}
        {!readOnlyMode && isUpdate && (
          <Grid item xs={6} sm={2} md={2} {...internalControlButtonsGridProps?.update}>
            <QCXButton
              color="secondary"
              onClick={handleVincular}
              disabled={disableButtons || isParentConsult || isLoading}
              fullWidth
              tooltip
              tooltipDescription={t('com.muralis.qcx.acoes.atualizar')}
            >
              {isLoading ? <QCXCircularProgress color="disabled" size={20} /> : <SaveIcon />}
            </QCXButton>
          </Grid>
        )}
        {isUpdate && (
          <Grid
            item
            xs={6}
            sm={readOnlyMode ? 4 : 2}
            md={readOnlyMode ? 4 : 2}
            {...internalControlButtonsGridProps?.clear}
          >
            <QCXButton
              variant="outlined"
              onClick={handleClearCurrentVinculo}
              tooltip
              tooltipDescription={t('com.muralis.qcx.acoes.limpar')}
              fullWidth
              disabled={(disableButtons && !isParentConsult) || !isUpdate || isLoading}
            >
              <ClearIcon />
            </QCXButton>
          </Grid>
        )}
        <Grid item xs={12}>
          <QCXDataGrid
            columns={dataGridColumns}
            rows={currentVinculosList}
            divProps={{
              ...dataGridDivProps,
              className: classes?.dataGridDiv,
            }}
          />
        </Grid>
      </Grid>
      <QCXConfirmDialog
        open={operationOfRemoveVinculo?.active}
        title={operationOfRemoveVinculo?.title}
        content={operationOfRemoveVinculo?.message}
        endContent={operationOfRemoveVinculo?.endMessage}
        onConfirm={operationOfRemoveVinculo.confirm}
        onClose={operationOfRemoveVinculo?.reset}
      />
    </>
  );
}
